import _ from 'lodash';
import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useForm, UseFormProps, UseFormReturn, FieldValues } from 'react-hook-form';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { setFormValues, clearForm, toggleTransient } from 'careand-redux/slices/rhfForms';

/**
 * A wrapper around useForm that syncs form state to Redux via the rhfForms slice.
 * the forms are persisted through ReduxPersist
 *
 * - `options.transient` - (default: false) uses redux to store the form but does not persist across refreshes
 * - `options.destroyOnUnmount` - (default: true) deletes the form from redux on unmount
 */
export function useReduxForm<T extends FieldValues>(
  formName: string,
  options: UseFormProps<T> & { transient?: boolean; destroyOnUnmount?: boolean } = {},
): UseFormReturn<T> {
  const dispatch = useAppDispatch();
  const persistedValues = useAppSelector((state) => state.rhfForms[formName]) as T | undefined;
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the last values this hook wrote to Redux, to distinguish external updates
  const lastSyncedRef = useRef<string>('');
  const hasAppliedPersistedRef = useRef(false);

  const methods = useForm<T>({
    ..._.omit(options, ['transient', 'destroyOnUnmount']),
    // Always use original defaults so reset() clears to empty state
  });

  // if persisted values exist, apply them (not as defaultValues)
  if (!hasAppliedPersistedRef.current) {
    if (persistedValues) {
      methods.reset(_.cloneDeep(persistedValues) as UseFormProps<T>['defaultValues'] as T, {
        keepDefaultValues: true,
      });
    }
    hasAppliedPersistedRef.current = true;
  }

  const resetOverride = useCallback(
    ((args: any, options?: any) => {
      // Cancel any pending throttled sync so stale values don't overwrite the reset
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
        throttleRef.current = null;
      }
      if (!args) {
        methods.reset();
        dispatch(clearForm(formName));
      } else {
        methods.reset(args, options);
        dispatch(
          setFormValues({
            formName,
            values: args,
          }),
        );
      }
    }) as typeof methods.reset,
    [methods.reset, formName],
  );

  useEffect(() => {
    if (options.transient) {
      dispatch(toggleTransient({ formName, transient: true }));
    }
  }, [dispatch, formName, options.transient]);

  // Watch all values and sync to Redux (throttled)
  useEffect(() => {
    const subscription = methods.watch((values) => {
      if (throttleRef.current) clearTimeout(throttleRef.current);
      throttleRef.current = setTimeout(() => {
        values = methods.getValues() as any;
        const serialized = JSON.stringify(values);
        lastSyncedRef.current = serialized;
        dispatch(setFormValues({ formName, values: values as Record<string, unknown> }));
      }, 300);
    });
    return () => {
      subscription.unsubscribe();
      if (throttleRef.current) clearTimeout(throttleRef.current);
    };
  }, [methods, methods.watch, dispatch, formName]);

  // Sync initial values to Redux on mount
  useEffect(() => {
    const sync = () => {
      const currentValues = methods.getValues();
      const serialized = JSON.stringify(currentValues);
      lastSyncedRef.current = serialized;
      dispatch(setFormValues({ formName, values: currentValues as Record<string, unknown> }));
    };
    sync();

    return () => {
      if (options.destroyOnUnmount ?? true) {
        dispatch(clearForm(formName));
      } else {
        sync();
      }
    };
  }, [formName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect external Redux changes and sync back to RHF
  useEffect(() => {
    if (!persistedValues) return;
    // Skip if a local change hasn't synced to Redux yet — resetting now would wipe it
    const serialized = JSON.stringify(persistedValues);
    if (serialized !== lastSyncedRef.current) {
      lastSyncedRef.current = serialized;
      methods.reset(_.cloneDeep(persistedValues) as UseFormProps<T>['defaultValues'] as T, {
        keepDefaultValues: true,
      });
    }
  }, [persistedValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const methodsOverride = useMemo(
    () => ({
      ...methods,
      reset: resetOverride,
    }),
    [methods, resetOverride],
  );

  return methodsOverride;
}

export { clearForm } from 'careand-redux/slices/rhfForms';
export { setFormValues } from 'careand-redux/slices/rhfForms';
